/**
 * Live on-chain reader: `LaunchpadFactory` + `LaunchToken` → the `DemoToken` /
 * `DemoTrade` / `DemoHolder` shapes in `src/lib/demo/types.ts`.
 *
 * The whole point of this file is that the UI cannot tell the difference. Every
 * display aggregate is derived the same way `src/lib/demo/world.ts` derives it
 * (see `applyCurveState` and `safetyScore` there), so the synthetic market and a
 * real chain render identically — only the numbers change.
 *
 * ## Why the reads are shaped the way they are
 *
 * A token page needs ~27 getters. Issued naively that is 27 HTTP round trips per
 * token per refresh, which is unusable against a public RPC. Two mitigations:
 *
 *  1. **Multicall3** where the chain really has one (Base, Base Sepolia): all 27
 *     getters collapse into a single `eth_call`.
 *  2. **JSON-RPC batching** where it does not. A bare `anvil` has no Multicall3
 *     and viem's `foundry` chain declares none. Rather than fire 27 requests, we
 *     re-create the client with `http(url, { batch: … })`: the same 27
 *     `eth_call`s leave as one HTTP request.
 *
 * Every read in this file goes through that locally-built client, never through
 * the wagmi client it is handed — see {@link batchClientFor} for why that is a
 * correctness requirement and not just a speed one.
 *
 * Logs are read in **one** `getLogs` for the whole market — viem accepts an
 * address array and an event array, so twelve tokens × five event types is a
 * single request, not sixty. That single scan is what supplies every field the
 * getters cannot: volume, deltas, trade counts, floor lifts, buybacks, burns.
 *
 * ## Honesty rule
 *
 * `description`, `tags`, `links`, `creatorHandle` and `traderHandle` do not exist
 * on-chain. They are left empty rather than invented — a real token must never be
 * presented with made-up copy. `hue` and `glyph` are the one exception, and only
 * because they are *generated marks*, derived deterministically from the address:
 * they assert nothing about the token.
 */

import {
  parseAbi,
  type Abi,
  type AbiEvent,
  type Address,
  type Log,
  type PublicClient,
} from 'viem';
import { createPublicClient, http } from 'viem';
import { LaunchTokenAbi } from '@/generated';
import { hueFromAddress, weiToNumber } from '@/lib/format';
import {
  BLOCK_MS,
  curveStateFromToken,
  graduationProgress as graduationProgressOf,
  type CurveState,
} from '@/lib/demo/curve';
import type {
  ActivityKind,
  DemoActivity,
  DemoHolder,
  DemoPosition,
  DemoToken,
  DemoTrade,
  Hex,
  HolderLabel,
  ProtocolStats,
  SeriesPoint,
  TokenPhase,
  TradeSide,
} from '@/lib/demo/types';

const HOUR = 3_600_000;
const DAY = 86_400_000;

const clamp = (n: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, n));
const finite = (n: number): number => (Number.isFinite(n) ? n : 0);

/* ────────────────────────────────────────────────────────────────────────────
   ABIs
   ──────────────────────────────────────────────────────────────────────────── */

/**
 * `src/generated/LaunchpadFactory.ts` is STALE: its `TokenCreated` still carries
 * the old `(name, symbol, salt)` tuple, while the deployed contract emits the
 * full flattened config snapshot. Decoding with the generated ABI silently
 * matches nothing. So the two factory reads and the event this file needs are
 * declared here, against `LaunchpadFactory.sol` as it actually is.
 *
 * The generated `LaunchTokenAbi` *is* current (verified getter-by-getter against
 * the contract), so that one is imported rather than duplicated.
 */
const FACTORY_ABI = parseAbi([
  'function allTokensLength() view returns (uint256)',
  'function allTokens(uint256) view returns (address)',
  'event TokenCreated(address indexed token, address indexed creator, string name, string symbol, bytes32 salt, uint256 virtualQuoteReserve, uint256 virtualTokenReserve, uint16 feeBPS, uint16 floorShareBPS, uint256 graduationQuoteThreshold, uint16 antiSnipeStartBPS, uint32 antiSnipeWindow, uint16 buybackFeeBPS, uint256 buybackThreshold)',
]);

const TOKEN_ABI = LaunchTokenAbi as unknown as Abi;

function tokenEvent(name: string): AbiEvent {
  const hit = (LaunchTokenAbi as readonly { type: string; name?: string }[]).find(
    (item) => item.type === 'event' && item.name === name,
  );
  if (!hit) throw new Error(`LaunchToken ABI is missing the ${name} event`);
  return hit as unknown as AbiEvent;
}

const EV_TRADE = tokenEvent('Trade');
const EV_FLOOR_RAISED = tokenEvent('FloorRaised');
const EV_ANTI_SNIPE = tokenEvent('AntiSnipeFee');
const EV_REDEEMED = tokenEvent('Redeemed');
const EV_BUYBACK = tokenEvent('BuybackExecuted');
const EV_TRANSFER = tokenEvent('Transfer');

/** The five events that make up a token's trade tape and its activity counters. */
const ACTIVITY_EVENTS: AbiEvent[] = [EV_TRADE, EV_FLOOR_RAISED, EV_ANTI_SNIPE, EV_REDEEMED, EV_BUYBACK];

/** Every getter a `DemoToken` needs, in one batch. Order is the decode order. */
const TOKEN_READS = [
  'name',
  'symbol',
  'decimals',
  'totalSupply',
  'creator',
  'graduated',
  'creationBlock',
  'feeBPS',
  'floorShareBPS',
  'antiSnipeStartBPS',
  'antiSnipeWindow',
  'graduationQuoteThreshold',
  'buybackFeeBPS',
  'buybackThreshold',
  'virtualQuoteReserve',
  'virtualTokenReserve',
  'quoteReserve',
  'tokenReserve',
  'floorPrice',
  'floorPriceLive',
  'floorReserve',
  'snipeSurplus',
  'treasuryReserve',
  'redeemableSupply',
  'spotPrice',
  'tradingStopped',
  'antiSnipeBPS',
] as const;

type TokenRead = (typeof TOKEN_READS)[number];

/* ────────────────────────────────────────────────────────────────────────────
   Transport: multicall where possible, batched JSON-RPC where not
   ──────────────────────────────────────────────────────────────────────────── */

/**
 * The read client for EVERYTHING in this file. Two reasons, and the second one is
 * a genuine footgun rather than an optimisation:
 *
 *  1. **JSON-RPC batching.** 27 `eth_call`s leave as one HTTP request.
 *  2. **It bypasses wagmi's implicit multicall routing.** wagmi's `createConfig`
 *     builds its clients with `batch: { multicall: true }`, so viem silently
 *     rewrites every `readContract` on that client into a Multicall3 `aggregate3`
 *     whenever the chain declares one. On a chain that declares a Multicall3 that
 *     is not actually deployed, that call returns `0x` and every read fails with
 *     "Cannot decode zero data" — while a raw `eth_call` to the same address
 *     answers perfectly. A locally-built client has no such rewriting, so reads
 *     go where they are addressed.
 *
 * So: never issue a read on the wagmi client directly. Use this one. Multicall is
 * still used, but only where it is asked for EXPLICITLY (`tryMulticall`), where a
 * failure is visible and handled.
 *
 * Cached per chain+URL: rebuilding one per refresh would throw away viem's
 * request-coalescing window, which is the entire point of (1).
 */
const batchClients = new Map<string, PublicClient>();

function batchClientFor(client: PublicClient): PublicClient {
  const transport = client.transport as { url?: unknown };
  const url = typeof transport.url === 'string' ? transport.url : undefined;
  if (!url) return client; // custom/injected transport — nothing to re-create
  // Keyed by chain AND url: the cached client carries a `chain`, so keying on url
  // alone hands a caller on chain B a client still pinned to chain A whenever two
  // chains share an endpoint (a fork pointed at a local node, mainly). That made
  // `multicall` consult the wrong chain's contract table.
  const cacheKey = `${client.chain?.id ?? 0}|${url}`;
  const hit = batchClients.get(cacheKey);
  if (hit) return hit;
  const built = createPublicClient({
    chain: client.chain,
    // 12ms is long enough to sweep up a whole token's getters, short enough that
    // it never shows up as latency.
    transport: http(url, { batch: { wait: 12 } }),
  }) as PublicClient;
  batchClients.set(cacheKey, built);
  return built;
}

/**
 * Chains where the declared Multicall3 turned out not to be deployed.
 *
 * Without this the fallback is paid for on every token of every refresh: twelve
 * doomed `aggregate3` calls per 12s tick, forever. One failure is enough to know.
 */
const multicallUnavailable = new Set<number>();

function hasMulticall(client: PublicClient): boolean {
  const id = client.chain?.id;
  if (id !== undefined && multicallUnavailable.has(id)) return false;
  return Boolean(client.chain?.contracts?.multicall3?.address);
}

type CallResult = { status: 'success'; result: unknown } | { status: 'failure'; error: unknown };

/**
 * Runs a batch of reads against one contract. Failures are per-call, never fatal:
 * a single reverting getter must not blank the whole token.
 */
async function readBatch(
  client: PublicClient,
  address: Address,
  functionNames: readonly string[],
): Promise<CallResult[]> {
  const contracts = functionNames.map((functionName) => ({ address, abi: TOKEN_ABI, functionName }));

  if (hasMulticall(client)) {
    const results = await tryMulticall(client, contracts);
    if (results) return results;
  }

  const batched = batchClientFor(client);
  return Promise.all(
    functionNames.map(
      async (functionName): Promise<CallResult> => {
        try {
          const result = await batched.readContract({ address, abi: TOKEN_ABI, functionName });
          return { status: 'success', result };
        } catch (error) {
          return { status: 'failure', error };
        }
      },
    ),
  );
}

/**
 * Returns null when Multicall3 is unusable, so the caller can fall back.
 *
 * A chain can *declare* a Multicall3 that is not actually deployed — a fork, a
 * fresh devnet, a local anvil that someone pointed a mainnet chain config at.
 * Critically, `allowFailure: true` does NOT throw in that case: viem stamps the
 * aggregate call's own failure onto every entry and returns normally. Treating
 * that as data silently blanked every token. So a batch where *nothing* decoded
 * is read as "no multicall here" rather than "the contract has no state".
 */
async function tryMulticall(
  client: PublicClient,
  contracts: { address: Address; abi: Abi; functionName: string; args?: readonly unknown[] }[],
): Promise<CallResult[] | null> {
  try {
    // Through the batching client, so N tokens' multicalls leave as ONE HTTP
    // request rather than N — the aggregate3 calls coalesce just like plain reads.
    const results = (await batchClientFor(client).multicall({
      contracts,
      allowFailure: true,
      // viem's default `batchSize` (1024 bytes of calldata) would split 27 getters
      // into several `aggregate3` calls. 0 disables the split so it really is one.
      batchSize: 0,
    })) as unknown as CallResult[];
    if (results.length === 0) return null;
    if (results.some((r) => r.status === 'success')) return results;
  } catch {
    // fall through
  }
  const id = client.chain?.id;
  if (id !== undefined) multicallUnavailable.add(id);
  return null;
}

/* ────────────────────────────────────────────────────────────────────────────
   Typed accessors over a positional batch result
   ──────────────────────────────────────────────────────────────────────────── */

const READ_INDEX: Record<string, number> = Object.fromEntries(TOKEN_READS.map((n, i) => [n, i]));

class TokenState {
  constructor(private readonly results: CallResult[]) {}

  private raw(name: TokenRead): unknown {
    const hit = this.results[READ_INDEX[name] ?? -1];
    return hit && hit.status === 'success' ? hit.result : undefined;
  }

  big(name: TokenRead): bigint {
    const v = this.raw(name);
    return typeof v === 'bigint' ? v : 0n;
  }

  num(name: TokenRead): number {
    const v = this.raw(name);
    if (typeof v === 'number') return v;
    if (typeof v === 'bigint') return Number(v);
    return 0;
  }

  str(name: TokenRead, fallback = ''): string {
    const v = this.raw(name);
    return typeof v === 'string' ? v : fallback;
  }

  bool(name: TokenRead): boolean {
    return this.raw(name) === true;
  }

  addr(name: TokenRead): Hex {
    const v = this.raw(name);
    return typeof v === 'string' && v.startsWith('0x') ? (v as Hex) : ZERO_ADDRESS;
  }

  /** True when nothing decoded — the address is not a LaunchToken at all. */
  get empty(): boolean {
    return this.results.every((r) => r.status === 'failure');
  }
}

const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000' as Hex;

/* ────────────────────────────────────────────────────────────────────────────
   Generated marks (the only synthesised fields, and they assert nothing)
   ──────────────────────────────────────────────────────────────────────────── */

/**
 * Neutral geometric marks. Deliberately not emoji or letters: a generated glyph
 * must read as "the app made this up", never as branding the creator chose.
 */
const GLYPHS = ['◆', '●', '▲', '■', '◇', '○', '△', '□', '◈', '◉', '◐', '◑', '⬢', '⬡', '✦', '✧'];

function glyphFromAddress(address: string): string {
  let h = 0;
  for (let i = 2; i < address.length; i++) h = (h * 31 + address.charCodeAt(i)) % 1_000_003;
  return GLYPHS[h % GLYPHS.length]!;
}

/* ────────────────────────────────────────────────────────────────────────────
   Block timestamps
   ──────────────────────────────────────────────────────────────────────────── */

/**
 * Logs carry no timestamp, and every `ts` in the data model is ms epoch. Block
 * timestamps are immutable once mined, so they are cached forever — a refresh
 * only ever pays for blocks it has not seen.
 *
 * Above {@link MAX_TIMESTAMP_FETCH} distinct blocks we stop asking and
 * interpolate linearly between the oldest and newest known anchors instead.
 * That is exact on a constant-block-time chain (Base is 2s) and monotone
 * everywhere, which is all the charts require.
 */
const MAX_TIMESTAMP_FETCH = 256;
const blockTimeCache = new Map<string, number>();

export async function fetchBlockTimestamps(
  client: PublicClient,
  blocks: Iterable<bigint>,
): Promise<Map<bigint, number>> {
  const chainKey = client.chain?.id ?? 0;
  const wanted = [...new Set([...blocks])].sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
  const out = new Map<bigint, number>();
  const missing: bigint[] = [];

  for (const b of wanted) {
    const hit = blockTimeCache.get(`${chainKey}:${b}`);
    if (hit !== undefined) out.set(b, hit);
    else missing.push(b);
  }
  if (missing.length === 0) return out;

  const batched = batchClientFor(client);

  // The two anchors are always fetched: they are what interpolation needs, and
  // they are the cheapest possible fallback when the set is too large.
  const anchors = missing.length > MAX_TIMESTAMP_FETCH ? [missing[0]!, missing[missing.length - 1]!] : missing;

  const fetched = await Promise.all(
    anchors.map(async (blockNumber) => {
      try {
        const block = await batched.getBlock({ blockNumber, includeTransactions: false });
        return [blockNumber, Number(block.timestamp) * 1000] as const;
      } catch {
        return [blockNumber, undefined] as const;
      }
    }),
  );

  for (const [blockNumber, ts] of fetched) {
    if (ts === undefined) continue;
    blockTimeCache.set(`${chainKey}:${blockNumber}`, ts);
    out.set(blockNumber, ts);
  }

  if (anchors.length === missing.length) return out;

  // Interpolate the rest. Both anchors resolved in the normal case; if either is
  // missing we fall back to a nominal block time so `ts` is still ordered.
  const lo = missing[0]!;
  const hi = missing[missing.length - 1]!;
  const loTs = out.get(lo);
  const hiTs = out.get(hi);
  const span = Number(hi - lo) || 1;
  for (const b of missing) {
    if (out.has(b)) continue;
    const f = Number(b - lo) / span;
    const ts =
      loTs !== undefined && hiTs !== undefined
        ? loTs + (hiTs - loTs) * f
        : (hiTs ?? loTs ?? Date.now()) - Number(hi - b) * BLOCK_MS;
    out.set(b, Math.round(ts));
  }
  return out;
}

/* ────────────────────────────────────────────────────────────────────────────
   Token discovery
   ──────────────────────────────────────────────────────────────────────────── */

export interface TokenMeta {
  address: Hex;
  creator: Hex;
  name?: string;
  symbol?: string;
  /** Block the token was created in — the natural `fromBlock` for its own logs. */
  creationBlock?: bigint;
}

/**
 * How far back the market-wide activity scan reaches: ~7 days at 2s/block, which
 * is exactly the widest window the UI shows (`change7d`). It used to be 83 days,
 * which bought nothing and cost a chunked walk of a dozen requests per refresh.
 */
const ACTIVITY_LOOKBACK_BLOCKS = 302_400n;
/** Discovery wants the factory's whole life, so it reaches much further back. */
const DISCOVERY_LOOKBACK_BLOCKS = 20_000_000n;
/** Conservative range for the chunked walk — under what restrictive RPCs allow. */
const LOG_CHUNK_BLOCKS = 45_000n;
const MAX_LOG_CHUNKS = 12;

/**
 * The token set, with each token's creation block where we can get it.
 *
 * `TokenCreated` logs are the cheap path AND the only one that yields a creation
 * block — which everything downstream wants, because it is the natural `fromBlock`
 * for that token's own logs.
 *
 * But logs alone are NOT trustworthy for *completeness*. A node will only serve a
 * bounded block range, so the scan reaches back a bounded distance; against the
 * real Base Sepolia factory that window stopped ~12 days short of the deployment
 * and the scan reported one token while `allTokensLength()` knew about more.
 * Silently under-reporting the market is worse than one extra round trip, so
 * `allTokensLength()` is read up front and the array is used to fill any gap.
 */
export async function fetchTokenAddresses(client: PublicClient, factory: Address): Promise<TokenMeta[]> {
  const latest = await client.getBlockNumber();
  const fromBlock = latest > DISCOVERY_LOOKBACK_BLOCKS ? latest - DISCOVERY_LOOKBACK_BLOCKS : 0n;

  const batched = batchClientFor(client);
  const count = Number(
    (await batched.readContract({
      address: factory,
      abi: FACTORY_ABI,
      functionName: 'allTokensLength',
    })) as bigint,
  );
  if (count === 0) return [];

  const seen = new Map<string, TokenMeta>();
  try {
    // ONE wide attempt, never a chunked walk. Chunking a factory-lifetime range
    // is the worst of both worlds: a dozen requests that still stop short of the
    // deployment block. The array read below is both cheaper and complete, so a
    // node that refuses the wide range should fall straight through to it.
    const logs = (await batchClientFor(client).getLogs({
      address: factory,
      event: FACTORY_ABI[2] as AbiEvent,
      fromBlock,
      toBlock: latest,
    } as never)) as Log[];
    for (const log of logs) {
      const args = (log as { args?: Record<string, unknown> }).args ?? {};
      const address = args.token as Hex | undefined;
      if (!address) continue;
      seen.set(address.toLowerCase(), {
        address,
        creator: (args.creator as Hex) ?? ZERO_ADDRESS,
        name: typeof args.name === 'string' ? args.name : undefined,
        symbol: typeof args.symbol === 'string' ? args.symbol : undefined,
        creationBlock: log.blockNumber ?? undefined,
      });
    }
  } catch {
    // A restricted RPC, or a range it refuses. The array read covers us.
  }

  if (seen.size >= count) return [...seen.values()];

  // Fill the gap. Batched, so this is one HTTP request rather than `count` of
  // them. Tokens recovered this way have no creation block, and the callers
  // handle that by falling back to the lookback window.
  const addresses = (await Promise.all(
    Array.from({ length: count }, (_, i) =>
      batched.readContract({ address: factory, abi: FACTORY_ABI, functionName: 'allTokens', args: [BigInt(i)] }),
    ),
  )) as Hex[];

  for (const address of addresses) {
    const key = address.toLowerCase();
    if (!seen.has(key)) seen.set(key, { address, creator: ZERO_ADDRESS });
  }
  return [...seen.values()];
}

/**
 * RPC endpoints that refused a wide `getLogs` range. Remembered so the optimistic
 * single request is only ever wasted once per endpoint.
 */
const wideRangeRejected = new Set<string>();

/**
 * `getLogs` over a possibly-huge range.
 *
 * Optimistic by design: most nodes will serve a very wide range when the filter
 * names a specific address and topic, so the whole window is attempted as ONE
 * request first. Only an endpoint that actually refuses gets the chunked walk —
 * chunking unconditionally cost 24 requests per refresh against Base Sepolia,
 * which happily answers the same query in one.
 */
async function getLogsChunked(
  client: PublicClient,
  params: {
    address: Address | Address[];
    event?: AbiEvent;
    events?: AbiEvent[];
    fromBlock: bigint;
    toBlock: bigint;
  },
): Promise<Log[]> {
  const { fromBlock, toBlock } = params;
  if (toBlock < fromBlock) return [];

  const transport = client.transport as { url?: unknown };
  const endpoint = typeof transport.url === 'string' ? transport.url : 'default';
  const total = toBlock - fromBlock + 1n;

  if (total <= LOG_CHUNK_BLOCKS || !wideRangeRejected.has(endpoint)) {
    try {
      return (await batchClientFor(client).getLogs(params as never)) as Log[];
    } catch (error) {
      if (total <= LOG_CHUNK_BLOCKS) throw error;
      wideRangeRejected.add(endpoint);
    }
  }

  // Walk backwards from head: recent history is what the UI shows, and a capped
  // walk is what keeps a cold start on a busy chain from being unbounded.
  const ranges: { from: bigint; to: bigint }[] = [];
  let to = toBlock;
  while (to >= fromBlock && ranges.length < MAX_LOG_CHUNKS) {
    const from = to - LOG_CHUNK_BLOCKS + 1n > fromBlock ? to - LOG_CHUNK_BLOCKS + 1n : fromBlock;
    ranges.push({ from, to });
    if (from === fromBlock) break;
    to = from - 1n;
  }
  ranges.reverse();

  const batched = batchClientFor(client);
  const out: Log[] = [];
  for (const range of ranges) {
    const logs = (await batched.getLogs({ ...params, fromBlock: range.from, toBlock: range.to } as never)) as Log[];
    out.push(...logs);
  }
  return out;
}

/* ────────────────────────────────────────────────────────────────────────────
   One token
   ──────────────────────────────────────────────────────────────────────────── */

/**
 * Everything the trade tape and the activity counters supply. Split out from the
 * getters because it comes from a *log* scan, which is priced per market rather
 * than per token — see {@link fetchMarket}.
 */
export interface TokenActivity {
  volume24hEth: number;
  trades24h: number;
  floorLifts: number;
  buybacks: number;
  ethBurnedEth: number;
  change1h: number;
  change24h: number;
  change7d: number;
  holderCount: number;
  /** Only used for the safety score's concentration term. */
  holders: DemoHolder[];
}

const EMPTY_ACTIVITY: TokenActivity = {
  volume24hEth: 0,
  trades24h: 0,
  floorLifts: 0,
  buybacks: 0,
  ethBurnedEth: 0,
  change1h: 0,
  change24h: 0,
  change7d: 0,
  holderCount: 0,
  holders: [],
};

/**
 * One token, fully populated. Every getter goes out in a single batch (one
 * `eth_call` via Multicall3, or one batched JSON-RPC request without it).
 *
 * `activity` is optional: without it the log-derived fields (volume, deltas,
 * counts, holder count) come back as zero rather than as a guess, and the safety
 * score's concentration term reads as "unknown", not "well distributed".
 */
export async function fetchToken(
  client: PublicClient,
  address: Address,
  meta?: TokenMeta,
  opts: { blockNumber?: bigint; blockTimestamps?: Map<bigint, number>; activity?: TokenActivity } = {},
): Promise<DemoToken | undefined> {
  const state = new TokenState(await readBatch(client, address, TOKEN_READS));
  if (state.empty) return undefined;

  const blockNumber = opts.blockNumber ?? (await client.getBlockNumber());
  const creationBlock = state.big('creationBlock') || meta?.creationBlock || 0n;
  const createdAt =
    opts.blockTimestamps?.get(creationBlock) ??
    (await fetchBlockTimestamps(client, [creationBlock])).get(creationBlock) ??
    Date.now();

  return buildToken({
    address: address as Hex,
    state,
    meta,
    createdAt,
    blockNumber,
    activity: opts.activity ?? EMPTY_ACTIVITY,
  });
}

function buildToken(input: {
  address: Hex;
  state: TokenState;
  meta?: TokenMeta;
  createdAt: number;
  blockNumber: bigint;
  activity: TokenActivity;
}): DemoToken {
  const { address, state, meta, createdAt, blockNumber, activity } = input;

  const graduated = state.bool('graduated');
  const tradingStopped = state.bool('tradingStopped');
  const antiSnipeWindow = state.num('antiSnipeWindow');
  const creationBlock = state.big('creationBlock') || meta?.creationBlock || 0n;
  const elapsedBlocks = blockNumber > creationBlock ? Number(blockNumber - creationBlock) : 0;

  // `antiSnipeBPS()` is the contract's own decayed rate, so the effective fee is
  // exact here rather than estimated from wall-clock the way the demo curve has
  // to estimate it (DESIGN §10: the window is measured in BLOCKS).
  const antiSnipeBPS = state.num('antiSnipeBPS');
  const feeBPS = state.num('feeBPS');

  const shell: DemoToken = {
    address,
    name: state.str('name', meta?.name ?? 'Unknown'),
    symbol: state.str('symbol', meta?.symbol ?? '???'),
    // Not on chain. Empty, never invented.
    description: '',
    hue: hueFromAddress(address),
    glyph: glyphFromAddress(address),
    tags: [],
    links: {},

    creator: state.addr('creator') !== ZERO_ADDRESS ? state.addr('creator') : (meta?.creator ?? ZERO_ADDRESS),
    creatorHandle: '',
    createdAt,

    phase: 'trading',
    graduated,
    tradingStopped,

    totalSupply: state.big('totalSupply'),
    tokenReserve: state.big('tokenReserve'),
    quoteReserve: state.big('quoteReserve'),
    virtualTokenReserve: state.big('virtualTokenReserve'),
    virtualQuoteReserve: state.big('virtualQuoteReserve'),
    floorReserve: state.big('floorReserve'),
    treasuryReserve: state.big('treasuryReserve'),
    snipeSurplus: state.big('snipeSurplus'),
    redeemableSupply: state.big('redeemableSupply'),
    graduationQuoteThreshold: state.big('graduationQuoteThreshold'),
    buybackThreshold: state.big('buybackThreshold'),

    feeBPS,
    floorShareBPS: state.num('floorShareBPS'),
    buybackFeeBPS: state.num('buybackFeeBPS'),
    antiSnipeStartBPS: state.num('antiSnipeStartBPS'),
    antiSnipeWindow,
    effectiveFeeBPS: feeBPS + antiSnipeBPS,

    spotPrice: state.big('spotPrice'),
    floorPrice: state.big('floorPrice'),

    // Filled in below, once the float curve state exists.
    marketCapEth: 0,
    liquidityEth: 0,
    raisedEth: 0,
    floorLockedEth: 0,
    floorRatio: 0,
    graduationProgress: 0,
    volume24hEth: activity.volume24hEth,
    change1h: activity.change1h,
    change24h: activity.change24h,
    change7d: activity.change7d,
    holderCount: activity.holderCount,
    trades24h: activity.trades24h,
    floorLifts: activity.floorLifts,
    buybacks: activity.buybacks,
    ethBurnedEth: activity.ethBurnedEth,
    safetyScore: 0,
  };

  // The aggregates below mirror `world.ts#applyCurveState` line for line, which is
  // what makes the two sources visually interchangeable.
  const curve: CurveState = curveStateFromToken(shell);
  const spot = weiToNumber(shell.spotPrice);
  const floor = weiToNumber(shell.floorPrice);
  const raisedEth = Math.max(0, curve.vQuote - curve.vQuote0);

  shell.marketCapEth = spot * weiToNumber(shell.totalSupply);
  shell.liquidityEth = raisedEth;
  shell.raisedEth = raisedEth;
  shell.floorLockedEth = weiToNumber(shell.floorReserve);
  shell.floorRatio = spot > 0 ? clamp(floor / spot, 0, 1) : 0;
  shell.graduationProgress = graduated ? 1 : graduationProgressOf(curve);
  shell.phase = derivePhase({
    graduated,
    tradingStopped,
    elapsedBlocks,
    antiSnipeWindow,
    graduationProgress: shell.graduationProgress,
  });
  shell.safetyScore = safetyScoreFor(shell, activity.holders);

  return shell;
}

/**
 * Same rule as `world.ts#phaseFor`, but the anti-snipe test is done in BLOCKS —
 * which is the unit the contract actually uses (DESIGN §10). The demo has to
 * approximate this from wall-clock; here we do not.
 */
function derivePhase(input: {
  graduated: boolean;
  tradingStopped: boolean;
  elapsedBlocks: number;
  antiSnipeWindow: number;
  graduationProgress: number;
}): TokenPhase {
  if (input.graduated) return 'graduated';
  if (input.tradingStopped) return 'halted';
  if (input.antiSnipeWindow > 0 && input.elapsedBlocks < input.antiSnipeWindow) return 'launching';
  if (input.graduationProgress >= 0.85) return 'graduating';
  return 'trading';
}

/**
 * Byte-for-byte the weighting in `world.ts#safetyScore`, so the meter reads the
 * same on both sources. With no holder data the concentration term contributes
 * nothing rather than full marks: an unknown distribution must not score as a
 * good one.
 */
export function safetyScoreFor(token: DemoToken, holders: DemoHolder[], now = Date.now()): number {
  const known = holders.length > 0;
  const top10 = holders
    .filter((h) => h.label !== 'protocol')
    .slice(0, 10)
    .reduce((a, h) => a + h.share, 0);
  const ageDays = (now - token.createdAt) / DAY;

  const score =
    34 * clamp(token.floorRatio / 0.6, 0, 1) +
    (known ? 20 * clamp((1 - top10) / 0.75, 0, 1) : 0) +
    18 * clamp(token.graduationProgress, 0, 1) +
    14 * clamp(ageDays / 30, 0, 1) +
    8 * clamp(token.buybacks / 6, 0, 1) +
    6 * clamp(token.raisedEth / 50, 0, 1);

  return Math.round(clamp(token.tradingStopped && !token.graduated ? score - 25 : score, 1, 98));
}

/* ────────────────────────────────────────────────────────────────────────────
   Trades
   ──────────────────────────────────────────────────────────────────────────── */

type Args = Record<string, unknown>;

function argsOf(log: Log): Args {
  return ((log as { args?: Args }).args ?? {}) as Args;
}
function bigArg(a: Args, key: string): bigint {
  const v = a[key];
  return typeof v === 'bigint' ? v : 0n;
}
function addrArg(a: Args, key: string): Hex {
  const v = a[key];
  return typeof v === 'string' && v.startsWith('0x') ? (v as Hex) : ZERO_ADDRESS;
}

/**
 * A token's tape, newest first, from `Trade` / `Redeemed` / `BuybackExecuted`,
 * annotated with `FloorRaised` and `AntiSnipeFee` from the same transaction.
 *
 * One `getLogs` for all five event types.
 */
export async function fetchTrades(
  client: PublicClient,
  address: Address,
  fromBlock: bigint,
  opts: { toBlock?: bigint; blockTimestamps?: Map<bigint, number> } = {},
): Promise<DemoTrade[]> {
  const toBlock = opts.toBlock ?? (await client.getBlockNumber());
  const logs = await getLogsChunked(client, {
    address,
    events: ACTIVITY_EVENTS,
    fromBlock,
    toBlock,
  });
  const timestamps =
    opts.blockTimestamps ??
    (await fetchBlockTimestamps(
      client,
      logs.map((l) => l.blockNumber ?? 0n),
    ));
  return tradesFromLogs(address as Hex, logs, timestamps);
}

/**
 * Chain order for two trades, newest first. Sort with this, never with `ts`.
 *
 * `ts` is a BLOCK timestamp, so every trade in the same block shares one — and
 * ties are the normal case, not an edge case: a busy chain packs many trades per
 * block, and a local anvil mined the whole seeded history inside two wall-clock
 * seconds. Ordering a tape by `ts` therefore leaves same-block trades in
 * arbitrary order, which turned a real monotone price path into noise (a 10x
 * "high" and a -85% "change" that never happened).
 *
 * {@link tradesFromLogs} encodes `(blockNumber, logIndex)` as a fixed-width
 * prefix of `id`, so a plain string compare IS chain order.
 */
export function byChainOrder(a: DemoTrade, b: DemoTrade): number {
  return b.id < a.id ? -1 : b.id > a.id ? 1 : 0;
}

/**
 * The `floorLiftEth` on a buy is `Trade.floorAmount` — the ETH actually routed to
 * the floor in that transaction — but it is only *set* when a `FloorRaised` shares
 * the tx, i.e. when the floor genuinely ratcheted. `FloorRaised` itself carries an
 * absolute `floorReserve`, not a delta, so it can flag the lift but cannot size it.
 */
export function tradesFromLogs(
  tokenAddress: Hex,
  logs: readonly Log[],
  timestamps: Map<bigint, number>,
): DemoTrade[] {
  const floorTxs = new Set<string>();
  const snipeTxs = new Set<string>();
  for (const log of logs) {
    const name = (log as { eventName?: string }).eventName;
    if (!log.transactionHash) continue;
    if (name === 'FloorRaised') floorTxs.add(log.transactionHash);
    else if (name === 'AntiSnipeFee') snipeTxs.add(log.transactionHash);
  }

  const out: DemoTrade[] = [];
  for (const log of logs) {
    const name = (log as { eventName?: string }).eventName;
    if (name !== 'Trade' && name !== 'Redeemed' && name !== 'BuybackExecuted') continue;

    const a = argsOf(log);
    const txHash = (log.transactionHash ?? `0x${'0'.repeat(64)}`) as Hex;
    const ts = timestamps.get(log.blockNumber ?? 0n) ?? Date.now();
    // Fixed-width `(block, logIndex)` prefix so a string compare is chain order
    // (see `byChainOrder`); the tx fragment is there to make a row traceable.
    const id = `${(log.blockNumber ?? 0n).toString().padStart(12, '0')}-${String(log.logIndex ?? 0).padStart(6, '0')}-${txHash.slice(2, 12)}`;

    let side: TradeSide;
    let trader: Hex;
    let ethAmount: number;
    let tokenAmount: number;
    let floorLiftEth: number | undefined;

    if (name === 'Trade') {
      const isBuy = a.isBuy === true;
      side = isBuy ? 'buy' : 'sell';
      // On a buy the tokens land on `to`; on a sell they are burned from `trader`.
      // The position — and so the holder row — belongs to that address.
      trader = isBuy ? addrArg(a, 'to') : addrArg(a, 'trader');
      ethAmount = weiToNumber(bigArg(a, 'quoteAmount'));
      tokenAmount = weiToNumber(bigArg(a, 'tokenAmount'));
      if (isBuy && floorTxs.has(txHash)) floorLiftEth = weiToNumber(bigArg(a, 'floorAmount'));
    } else if (name === 'Redeemed') {
      side = 'redeem';
      trader = addrArg(a, 'holder');
      ethAmount = weiToNumber(bigArg(a, 'quoteOut'));
      tokenAmount = weiToNumber(bigArg(a, 'tokenAmount'));
    } else {
      side = 'buyback';
      trader = addrArg(a, 'caller');
      // The burn leg is the part that behaves like a trade; the floor leg is the lift.
      ethAmount = weiToNumber(bigArg(a, 'curveSpent'));
      tokenAmount = weiToNumber(bigArg(a, 'tokensBurned'));
      const contribution = weiToNumber(bigArg(a, 'floorContribution'));
      if (contribution > 0) floorLiftEth = contribution;
    }

    out.push({
      id,
      tokenAddress,
      side,
      trader,
      // No on-chain handle registry — left undefined rather than fabricated.
      traderHandle: undefined,
      ethAmount,
      tokenAmount,
      price: tokenAmount > 0 ? finite(ethAmount / tokenAmount) : 0,
      ts,
      txHash,
      floorLiftEth,
      antiSnipe: side === 'buy' && snipeTxs.has(txHash) ? true : undefined,
    });
  }

  // Newest first, matching the demo tape and every consumer's expectation.
  out.sort(byChainOrder);
  return out;
}

/* ────────────────────────────────────────────────────────────────────────────
   Holders
   ──────────────────────────────────────────────────────────────────────────── */

/**
 * Holder rows netted out of the trade tape.
 *
 * This is an APPROXIMATION and deliberately so: `Transfer` logs are the accurate
 * source (see {@link fetchHolders}), but scanning them for every token in the
 * market list would multiply the log cost by the number of tokens. Netting the
 * tape we already have costs nothing extra, and is exact for any address that
 * only ever interacted with the curve. It misses wallet-to-wallet transfers and
 * airdrops, so a row can read low, or be absent entirely.
 */
export function deriveHolders(trades: readonly DemoTrade[], token: DemoToken): DemoHolder[] {
  interface Acc {
    balance: number;
    cost: number;
    bought: number;
    firstBuyAt: number;
  }
  const acc = new Map<string, Acc>();

  // Oldest first so `firstBuyAt` and the running cost basis come out right — in
  // CHAIN order, not timestamp order, or same-block fills net out in the wrong
  // sequence and a partial exit corrupts the basis.
  const ordered = [...trades].sort(byChainOrder).reverse();
  for (const trade of ordered) {
    if (trade.side === 'buyback') continue; // protocol-funded burn, not a holder
    const key = trade.trader.toLowerCase();
    const entry = acc.get(key) ?? { balance: 0, cost: 0, bought: 0, firstBuyAt: trade.ts };
    if (trade.side === 'buy') {
      entry.balance += trade.tokenAmount;
      entry.cost += trade.ethAmount;
      entry.bought += trade.tokenAmount;
      if (entry.bought === trade.tokenAmount) entry.firstBuyAt = trade.ts;
    } else {
      const sold = Math.min(entry.balance, trade.tokenAmount);
      // Reduce basis proportionally, so avgEntry survives a partial exit.
      entry.cost = entry.balance > 0 ? entry.cost * (1 - sold / entry.balance) : 0;
      entry.balance -= sold;
    }
    acc.set(key, entry);
  }

  const rows = [...acc.entries()]
    .filter(([, v]) => v.balance > 1e-12)
    .map(([address, v]) => ({
      address: address as Hex,
      balance: v.balance,
      avgEntry: v.balance > 0 ? v.cost / v.balance : 0,
      firstBuyAt: v.firstBuyAt,
    }));

  return finishHolders(rows, token, {});
}

/**
 * Accurate holder rows from a full `Transfer` scan, plus `modules()` so the vault
 * and the migrated DEX position can be labelled honestly instead of guessed at.
 *
 * Cost: one `getLogs` plus one `eth_call` per token. Only ever called lazily, for
 * the token page that is actually open.
 */
export async function fetchHolders(
  client: PublicClient,
  token: DemoToken,
  fromBlock: bigint,
  opts: { toBlock?: bigint; trades?: readonly DemoTrade[]; blockTimestamps?: Map<bigint, number> } = {},
): Promise<DemoHolder[]> {
  const toBlock = opts.toBlock ?? (await client.getBlockNumber());
  const [logs, modules] = await Promise.all([
    getLogsChunked(client, { address: token.address as Address, event: EV_TRANSFER, fromBlock, toBlock }),
    readModules(client, token.address as Address),
  ]);

  const timestamps =
    opts.blockTimestamps ??
    (await fetchBlockTimestamps(
      client,
      logs.map((l) => l.blockNumber ?? 0n),
    ));

  const balances = new Map<string, bigint>();
  const firstSeen = new Map<string, number>();
  for (const log of logs) {
    const a = argsOf(log);
    const from = addrArg(a, 'from').toLowerCase();
    const to = addrArg(a, 'to').toLowerCase();
    const value = bigArg(a, 'value');
    const ts = timestamps.get(log.blockNumber ?? 0n) ?? Date.now();
    if (from !== ZERO_ADDRESS) balances.set(from, (balances.get(from) ?? 0n) - value);
    if (to !== ZERO_ADDRESS) {
      balances.set(to, (balances.get(to) ?? 0n) + value);
      if (!firstSeen.has(to)) firstSeen.set(to, ts);
    }
  }

  // Entry prices are not in a Transfer, so they come from the tape when we have
  // it. Without it `pnlEth` is 0 rather than invented.
  const entries = new Map<string, number>();
  if (opts.trades) {
    const byTrader = new Map<string, { cost: number; qty: number }>();
    for (const t of opts.trades) {
      if (t.side !== 'buy') continue;
      const key = t.trader.toLowerCase();
      const hit = byTrader.get(key) ?? { cost: 0, qty: 0 };
      hit.cost += t.ethAmount;
      hit.qty += t.tokenAmount;
      byTrader.set(key, hit);
    }
    for (const [key, v] of byTrader) if (v.qty > 0) entries.set(key, v.cost / v.qty);
  }

  const rows = [...balances.entries()]
    .filter(([, v]) => v > 0n)
    .map(([address, v]) => ({
      address: address as Hex,
      balance: weiToNumber(v),
      avgEntry: entries.get(address) ?? 0,
      firstBuyAt: firstSeen.get(address) ?? token.createdAt,
    }));

  return finishHolders(rows, token, modules);
}

interface Modules {
  vault?: Hex;
  migrator?: Hex;
}

async function readModules(client: PublicClient, address: Address): Promise<Modules> {
  try {
    const res = (await batchClientFor(client).readContract({
      address,
      abi: TOKEN_ABI,
      functionName: 'modules',
    })) as readonly unknown[];
    const at = (i: number): Hex | undefined => {
      const v = res[i];
      return typeof v === 'string' && v.startsWith('0x') && v !== ZERO_ADDRESS ? (v as Hex) : undefined;
    };
    return { vault: at(0), migrator: at(3) };
  } catch {
    return {};
  }
}

/**
 * Shared tail: shares, values, labels, sort, rank. Labels follow `world.ts` —
 * `creator` from the contract's own `creator()`, `vault` / `protocol` from
 * `modules()`, `treasury` for the token contract itself (that is where
 * `treasuryReserve` lives), `whale` for a large top-6 row.
 */
function finishHolders(
  rows: { address: Hex; balance: number; avgEntry: number; firstBuyAt: number }[],
  token: DemoToken,
  modules: Modules,
): DemoHolder[] {
  const totalSupply = weiToNumber(token.totalSupply);
  const spot = weiToNumber(token.spotPrice);
  const floor = weiToNumber(token.floorPrice);
  const creator = token.creator.toLowerCase();
  const vault = modules.vault?.toLowerCase();
  const migrator = modules.migrator?.toLowerCase();
  const self = token.address.toLowerCase();

  const holders: DemoHolder[] = rows.map((row) => {
    const key = row.address.toLowerCase();
    const share = totalSupply > 0 ? row.balance / totalSupply : 0;
    let label: HolderLabel | undefined;
    if (key === creator) label = 'creator';
    else if (vault && key === vault) label = 'vault';
    else if (migrator && key === migrator) label = 'protocol';
    else if (key === self) label = 'treasury';

    return {
      rank: 0,
      address: row.address,
      // No handle registry on chain.
      handle: undefined,
      balance: toWei(row.balance),
      share,
      valueEth: row.balance * spot,
      // A migrated DEX position was never floor-backed, so it redeems for nothing.
      floorValueEth: label === 'protocol' ? 0 : row.balance * floor,
      pnlEth: row.avgEntry > 0 ? row.balance * (spot - row.avgEntry) : 0,
      label,
      firstBuyAt: row.firstBuyAt,
    };
  });

  holders.sort((a, b) => b.share - a.share);
  holders.forEach((h, i) => {
    h.rank = i + 1;
    // `whale` is a *rank* label in world.ts, so it can only be applied post-sort.
    if (!h.label && h.share > 0.035 && i < 6) h.label = 'whale';
  });
  return holders;
}

/** `parseEther` rejects exponential strings, so scale by hand. */
function toWei(n: number): bigint {
  if (!Number.isFinite(n) || n <= 0) return 0n;
  return BigInt(Math.round(n * 1e6)) * 10n ** 12n;
}

/* ────────────────────────────────────────────────────────────────────────────
   Activity aggregation
   ──────────────────────────────────────────────────────────────────────────── */

/**
 * Every log-derived counter on a token, computed from its tape.
 *
 * The price deltas use the last execution price at or before each cutoff, which
 * is what a chart would show. A token with no trade older than the cutoff has no
 * delta to report and gets 0 — never a synthesised move.
 */
export function activityFromTrades(trades: readonly DemoTrade[], token: DemoToken, now = Date.now()): TokenActivity {
  const spot = weiToNumber(token.spotPrice);
  let volume24hEth = 0;
  let trades24h = 0;
  let floorLifts = 0;
  let buybacks = 0;
  let ethBurnedEth = 0;

  for (const t of trades) {
    if (t.ts >= now - DAY) {
      volume24hEth += t.ethAmount;
      trades24h += 1;
    }
    if (t.floorLiftEth !== undefined && t.floorLiftEth > 0) floorLifts += 1;
    if (t.side === 'buyback') {
      buybacks += 1;
      ethBurnedEth += t.ethAmount; // curveSpent — the leg that actually burns supply
    }
  }

  const holders = deriveHolders(trades, token);
  const change = (window: number): number => {
    const then = priceAt(trades, now - window);
    if (!(then > 0) || !(spot > 0)) return 0;
    return spot / then - 1;
  };

  return {
    volume24hEth,
    trades24h,
    floorLifts,
    buybacks,
    ethBurnedEth,
    change1h: change(HOUR),
    change24h: change(DAY),
    change7d: change(7 * DAY),
    holderCount: holders.length,
    holders,
  };
}

/**
 * Last execution price at or before `at`; 0 when the token has no trade that old
 * — in which case the caller reports no delta rather than inventing one.
 *
 * The tape is newest-first in chain order, so the first match walking forward is
 * the most recent qualifying trade. Doing this by max-timestamp instead would
 * pick an arbitrary trade out of the tied block.
 */
function priceAt(trades: readonly DemoTrade[], at: number): number {
  for (const t of trades) {
    if (t.ts <= at && t.price > 0) return t.price;
  }
  return 0;
}

/* ────────────────────────────────────────────────────────────────────────────
   Protocol aggregates
   ──────────────────────────────────────────────────────────────────────────── */

export interface ScannedTotals {
  /** Buy+sell volume over the scanned window. On a chain younger than the
   *  window (a local anvil) this genuinely IS all-time. */
  volumeScannedEth: number;
  /** Sum of `Redeemed.quoteOut`. */
  ethReturnedEth: number;
  /** Distinct addresses seen on any trade — a real count, not an estimate. */
  tradersCount: number;
  /** Windowed volume split either side of the 24h line, for the hero delta. */
  volumePrev24hEth: number;
  floorLockedPrev24hEth: number;
}

const EMPTY_TOTALS: ScannedTotals = {
  volumeScannedEth: 0,
  ethReturnedEth: 0,
  tradersCount: 0,
  volumePrev24hEth: 0,
  floorLockedPrev24hEth: 0,
};

/**
 * Protocol aggregates. Every field is a real sum over what was actually fetched —
 * the hero stats must not be able to contradict the list underneath them.
 *
 * `volumeAllTimeEth` is volume over the *scanned window*, not literally all time:
 * a full-history scan is not something a public RPC will serve for a busy chain.
 */
export function fetchStats(tokens: readonly DemoToken[], totals: ScannedTotals = EMPTY_TOTALS): ProtocolStats {
  const volume24hEth = tokens.reduce((a, t) => a + t.volume24hEth, 0);
  const floorLockedEth = tokens.reduce((a, t) => a + t.floorLockedEth, 0);
  return {
    tokensLaunched: tokens.length,
    volumeAllTimeEth: totals.volumeScannedEth,
    volume24hEth,
    floorLockedEth,
    ethReturnedEth: totals.ethReturnedEth,
    graduatedCount: tokens.filter((t) => t.graduated).length,
    tradersCount: totals.tradersCount,
    ethBurnedEth: tokens.reduce((a, t) => a + t.ethBurnedEth, 0),
    volume24hChange: totals.volumePrev24hEth > 0 ? volume24hEth / totals.volumePrev24hEth - 1 : 0,
    floorLockedChange:
      totals.floorLockedPrev24hEth > 0 ? floorLockedEth / totals.floorLockedPrev24hEth - 1 : 0,
  };
}

const FEED_CAP = 220;

/**
 * The cross-token firehose: real trades, newest first. `launch` entries come from
 * each token's creation block, so a fresh launch shows up even before it trades.
 * `note` text is fixed protocol copy, not commentary about the token.
 */
export function deriveFeed(
  tokens: readonly DemoToken[],
  trades: ReadonlyMap<string, DemoTrade[]>,
  cap = FEED_CAP,
): DemoActivity[] {
  const byAddress = new Map(tokens.map((t) => [t.address.toLowerCase(), t] as const));
  const out: DemoActivity[] = [];

  for (const [key, tape] of trades) {
    const token = byAddress.get(key);
    if (!token) continue;
    const mark = { address: token.address, name: token.name, symbol: token.symbol, hue: token.hue, glyph: token.glyph };
    for (const t of tape) {
      const kind: ActivityKind =
        t.floorLiftEth !== undefined && t.floorLiftEth > 0 && t.side === 'buyback'
          ? 'buyback'
          : (t.side as ActivityKind);
      out.push({
        id: `feed:${t.id}`,
        kind,
        token: mark,
        actor: t.trader,
        actorHandle: undefined,
        ethAmount: t.ethAmount,
        tokenAmount: t.tokenAmount,
        ts: t.ts,
        txHash: t.txHash,
        note: noteFor(kind, t),
      });
    }
  }

  for (const token of tokens) {
    out.push({
      id: `feed:launch:${token.address}`,
      kind: token.graduated ? 'graduate' : 'launch',
      token: { address: token.address, name: token.name, symbol: token.symbol, hue: token.hue, glyph: token.glyph },
      actor: token.creator,
      actorHandle: undefined,
      ts: token.createdAt,
      // The creation tx hash is not carried on the token; omit rather than fake one.
      txHash: `0x${'0'.repeat(64)}` as Hex,
      note: token.graduated ? `${token.symbol} graduated` : `${token.symbol} launched`,
    });
  }

  // Chain order breaks the (very common) timestamp tie within a block.
  out.sort((a, b) => b.ts - a.ts || (b.id < a.id ? -1 : b.id > a.id ? 1 : 0));
  return out.slice(0, cap);
}

function noteFor(kind: ActivityKind, trade: DemoTrade): string | undefined {
  if (kind === 'buyback') return 'Buyback and burn';
  if (kind === 'redeem') return 'Redeemed at floor';
  if (trade.floorLiftEth !== undefined && trade.floorLiftEth > 0) return 'Floor ratcheted up';
  if (trade.antiSnipe) return 'Anti-snipe window';
  return undefined;
}

/* ────────────────────────────────────────────────────────────────────────────
   Protocol floor-locked series
   ──────────────────────────────────────────────────────────────────────────── */

const LOCKED_POINTS = 48;

/**
 * Protocol-wide floor-locked over time, walked BACKWARDS from the one number we
 * know exactly — the current sum of `floorReserve` — undoing each event as we go.
 *
 * Backwards is the only direction that can be anchored: `FloorRaised` carries an
 * absolute `floorReserve`, so consecutive events for one token give an exact
 * increment, and `Redeemed.quoteOut` is an exact decrement. The oldest in-window
 * `FloorRaised` per token has no predecessor, so its increment is unknown and
 * treated as 0 — the series is flat before that point rather than wrong.
 */
export function buildFloorLockedSeries(
  tokens: readonly DemoToken[],
  logs: readonly Log[],
  timestamps: Map<bigint, number>,
  now = Date.now(),
): { series: SeriesPoint[]; prev24h: number } {
  const current = tokens.reduce((a, t) => a + t.floorLockedEth, 0);

  interface Step {
    ts: number;
    delta: number;
  }
  const steps: Step[] = [];
  const prevFloorReserve = new Map<string, number>();

  const ordered = [...logs].sort(
    (a, b) => Number((a.blockNumber ?? 0n) - (b.blockNumber ?? 0n)) || (a.logIndex ?? 0) - (b.logIndex ?? 0),
  );
  for (const log of ordered) {
    const name = (log as { eventName?: string }).eventName;
    const ts = timestamps.get(log.blockNumber ?? 0n) ?? now;
    const key = (log.address ?? '').toLowerCase();
    const a = argsOf(log);
    if (name === 'FloorRaised') {
      const value = weiToNumber(bigArg(a, 'floorReserve'));
      const prev = prevFloorReserve.get(key);
      if (prev !== undefined) steps.push({ ts, delta: value - prev });
      prevFloorReserve.set(key, value);
    } else if (name === 'Redeemed') {
      steps.push({ ts, delta: -weiToNumber(bigArg(a, 'quoteOut')) });
    }
  }

  const earliest = steps.length > 0 ? Math.min(...steps.map((s) => s.ts)) : now - 7 * DAY;
  const span = Math.max(HOUR, now - earliest);
  const step = span / (LOCKED_POINTS - 1);

  // Sample times, newest first, undoing every step that happened after each one.
  const sorted = [...steps].sort((x, y) => y.ts - x.ts);
  const series: SeriesPoint[] = new Array(LOCKED_POINTS);
  let running = current;
  let cursor = 0;
  for (let i = LOCKED_POINTS - 1; i >= 0; i--) {
    const t = Math.round(now - (LOCKED_POINTS - 1 - i) * step);
    while (cursor < sorted.length && sorted[cursor]!.ts > t) {
      running -= sorted[cursor]!.delta;
      cursor += 1;
    }
    series[i] = { t, v: Math.max(0, running) };
  }

  let prev = current;
  for (const s of sorted) {
    if (s.ts > now - DAY) prev -= s.delta;
  }
  return { series, prev24h: Math.max(0, prev) };
}

/* ────────────────────────────────────────────────────────────────────────────
   Whole-market load
   ──────────────────────────────────────────────────────────────────────────── */

export interface LiveMarket {
  chainId: number;
  blockNumber: bigint;
  tokens: DemoToken[];
  stats: ProtocolStats;
  feed: DemoActivity[];
  floorLockedSeries: SeriesPoint[];
  /** Windowed tapes keyed by lowercased address — enough for the list aggregates,
   *  superseded by the per-token lazy fetch on a token page. */
  trades: Map<string, DemoTrade[]>;
  /** Creation block per token, so a lazy per-token scan knows where to start. */
  creationBlocks: Map<string, bigint>;
}

/**
 * One market refresh.
 *
 * RPC cost, for N tokens:
 *   1  `eth_blockNumber`
 *   1  `eth_getLogs`  — TokenCreated (whole factory)
 *   1  `eth_getLogs`  — Trade/FloorRaised/AntiSnipeFee/Redeemed/BuybackExecuted
 *                       across ALL N token addresses at once
 *   N  `eth_call`     — one Multicall3 per token (27 getters each)
 *   0–k `eth_getBlockByNumber` for block timestamps not already cached; after the
 *       first load this is ~0, because block timestamps never change.
 *
 * With Multicall3 that is N+3 calls; the 12-token seeded anvil is 15 calls, and
 * because anvil has no Multicall3 the N `eth_call` batches coalesce into N HTTP
 * requests via JSON-RPC batching rather than 27N.
 */
export async function fetchMarket(client: PublicClient, factory: Address): Promise<LiveMarket> {
  const chainId = client.chain?.id ?? 0;
  const blockNumber = await client.getBlockNumber();
  const metas = await fetchTokenAddresses(client, factory);

  const creationBlocks = new Map<string, bigint>();
  for (const m of metas) if (m.creationBlock !== undefined) creationBlocks.set(m.address.toLowerCase(), m.creationBlock);

  if (metas.length === 0) {
    return {
      chainId,
      blockNumber,
      tokens: [],
      stats: fetchStats([]),
      feed: [],
      floorLockedSeries: [],
      trades: new Map(),
      creationBlocks,
    };
  }

  // One log scan for the whole market. `address` takes an array, so N tokens ×
  // 5 event types is a single request rather than 5N.
  const windowStart = blockNumber > ACTIVITY_LOOKBACK_BLOCKS ? blockNumber - ACTIVITY_LOOKBACK_BLOCKS : 0n;
  // Start at the OLDEST known creation block, but never at `blockNumber` just
  // because no creation block is known — seeding the reduce with the head block
  // collapsed the scan to a single block and zeroed every activity counter for
  // any market discovered through the array read.
  const known = [...creationBlocks.values()];
  const earliest = known.length > 0 ? known.reduce((a, b) => (b < a ? b : a)) : windowStart;
  const fromBlock = earliest > windowStart ? earliest : windowStart;

  let activityLogs: Log[] = [];
  try {
    activityLogs = await getLogsChunked(client, {
      address: metas.map((m) => m.address as Address),
      events: ACTIVITY_EVENTS,
      fromBlock,
      toBlock: blockNumber,
    });
  } catch {
    // A node that refuses multi-address getLogs leaves the counters at zero
    // rather than blanking the market — the getters still carry every price.
  }

  const timestamps = await fetchBlockTimestamps(client, [
    ...activityLogs.map((l) => l.blockNumber ?? 0n),
    ...creationBlocks.values(),
  ]);

  const logsByToken = new Map<string, Log[]>();
  for (const log of activityLogs) {
    const key = (log.address ?? '').toLowerCase();
    const bucket = logsByToken.get(key);
    if (bucket) bucket.push(log);
    else logsByToken.set(key, [log]);
  }

  const now = Date.now();
  const trades = new Map<string, DemoTrade[]>();
  for (const meta of metas) {
    const key = meta.address.toLowerCase();
    trades.set(key, tradesFromLogs(meta.address, logsByToken.get(key) ?? [], timestamps));
  }

  // Token reads run concurrently; each is one batch, so this is N requests.
  const loaded = await Promise.all(
    metas.map(async (meta) => {
      const key = meta.address.toLowerCase();
      const state = new TokenState(await readBatch(client, meta.address as Address, TOKEN_READS));
      if (state.empty) return undefined;
      const creationBlock = state.big('creationBlock') || meta.creationBlock || 0n;
      const createdAt = timestamps.get(creationBlock) ?? now;

      // Two passes: the activity counters need the token's live spot price to
      // compute deltas, and the token needs the counters. Cheap — both are pure.
      const base = buildToken({
        address: meta.address,
        state,
        meta,
        createdAt,
        blockNumber,
        activity: EMPTY_ACTIVITY,
      });
      const activity = activityFromTrades(trades.get(key) ?? [], base, now);
      return buildToken({ address: meta.address, state, meta, createdAt, blockNumber, activity });
    }),
  );

  const tokens = loaded.filter((t): t is DemoToken => t !== undefined);
  // Newest launch first — the same ordering the demo world presents.
  tokens.sort((a, b) => b.createdAt - a.createdAt);

  const { series, prev24h } = buildFloorLockedSeries(tokens, activityLogs, timestamps, now);

  const traders = new Set<string>();
  let volumeScannedEth = 0;
  let ethReturnedEth = 0;
  let volumePrev24hEth = 0;
  for (const tape of trades.values()) {
    for (const t of tape) {
      if (t.side === 'buy' || t.side === 'sell') {
        volumeScannedEth += t.ethAmount;
        traders.add(t.trader.toLowerCase());
        if (t.ts < now - DAY && t.ts >= now - 2 * DAY) volumePrev24hEth += t.ethAmount;
      }
      if (t.side === 'redeem') ethReturnedEth += t.ethAmount;
    }
  }

  return {
    chainId,
    blockNumber,
    tokens,
    stats: fetchStats(tokens, {
      volumeScannedEth,
      ethReturnedEth,
      tradersCount: traders.size,
      volumePrev24hEth,
      floorLockedPrev24hEth: prev24h,
    }),
    feed: deriveFeed(tokens, trades),
    floorLockedSeries: series,
    trades,
    creationBlocks,
  };
}

/* ────────────────────────────────────────────────────────────────────────────
   Wallet positions
   ──────────────────────────────────────────────────────────────────────────── */

/**
 * The connected wallet's positions, from one batched `balanceOf` sweep across
 * every known token.
 *
 * Cost basis comes from the tape we already hold, so `avgEntry` and `pnlEth` are
 * only as complete as the scanned window: a position opened before it, or
 * acquired by transfer rather than by buying, shows a zero basis and therefore a
 * zero P&L rather than a fabricated one. `valueEth` and `floorValueEth` are exact
 * either way, because they are just balance × price.
 */
export async function fetchPositions(
  client: PublicClient,
  tokens: readonly DemoToken[],
  trades: ReadonlyMap<string, DemoTrade[]>,
  account: Address,
): Promise<DemoPosition[]> {
  if (tokens.length === 0) return [];

  const contracts = tokens.map((t) => ({
    address: t.address as Address,
    abi: TOKEN_ABI,
    functionName: 'balanceOf',
    args: [account],
  }));

  // Same Multicall3 caveat as {@link readBatch}: a declared-but-absent one comes
  // back as an all-failure batch, which would read as "nobody holds anything".
  const viaMulticall = hasMulticall(client) ? await tryMulticall(client, contracts) : null;
  const results = viaMulticall ?? (await readBalancesBatched(client, tokens, account));

  const key = account.toLowerCase();
  const out: DemoPosition[] = [];
  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i]!;
    const hit = results[i];
    const raw = hit && hit.status === 'success' && typeof hit.result === 'bigint' ? hit.result : 0n;
    if (raw <= 0n) continue;

    const balance = weiToNumber(raw);
    let cost = 0;
    let qty = 0;
    for (const t of trades.get(token.address.toLowerCase()) ?? []) {
      if (t.side !== 'buy' || t.trader.toLowerCase() !== key) continue;
      cost += t.ethAmount;
      qty += t.tokenAmount;
    }
    const avgEntry = qty > 0 ? cost / qty : 0;
    const costEth = avgEntry * balance;
    const valueEth = balance * weiToNumber(token.spotPrice);

    out.push({
      token,
      balance: raw,
      avgEntry,
      costEth,
      valueEth,
      floorValueEth: balance * weiToNumber(token.floorPrice),
      pnlEth: costEth > 0 ? valueEth - costEth : 0,
      pnlPct: costEth > 0 ? (valueEth - costEth) / costEth : 0,
    });
  }

  out.sort((a, b) => b.valueEth - a.valueEth);
  return out;
}

async function readBalancesBatched(
  client: PublicClient,
  tokens: readonly DemoToken[],
  account: Address,
): Promise<CallResult[]> {
  const batched = batchClientFor(client);
  return Promise.all(
    tokens.map(async (t): Promise<CallResult> => {
      try {
        const result = await batched.readContract({
          address: t.address as Address,
          abi: TOKEN_ABI,
          functionName: 'balanceOf',
          args: [account],
        });
        return { status: 'success', result };
      } catch (error) {
        return { status: 'failure', error };
      }
    }),
  );
}

/* ────────────────────────────────────────────────────────────────────────────
   Quotes straight from the contract
   ──────────────────────────────────────────────────────────────────────────── */

export interface ChainQuote {
  tokensOut: bigint;
  quoteOut: bigint;
}

/**
 * `quoteBuyExactIn` / `quoteSellExactIn` are the ground truth — they include the
 * anti-snipe slice at the current block, which no client-side mirror can know for
 * certain. Both go out in one batch.
 */
export async function fetchQuotes(
  client: PublicClient,
  address: Address,
  grossIn: bigint,
  tokensIn: bigint,
): Promise<ChainQuote> {
  const batched = batchClientFor(client);
  const [tokensOut, quoteOut] = await Promise.all([
    grossIn > 0n
      ? (batched.readContract({
          address,
          abi: TOKEN_ABI,
          functionName: 'quoteBuyExactIn',
          args: [grossIn],
        }) as Promise<bigint>).catch(() => 0n)
      : Promise.resolve(0n),
    tokensIn > 0n
      ? (batched.readContract({
          address,
          abi: TOKEN_ABI,
          functionName: 'quoteSellExactIn',
          args: [tokensIn],
        }) as Promise<bigint>).catch(() => 0n)
      : Promise.resolve(0n),
  ]);
  return { tokensOut, quoteOut };
}
